import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Camera,
  ChevronRight,
  ChevronLeft,
  Upload,
  Plus,
  Trash2,
  X,
  Check,
  AlertCircle,
  FileSpreadsheet,
  Download,
  Loader2,
  ArrowLeft,
  CheckCircle,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";
import { AdminContext } from "../../../../context/AdminContext";
import {
  downloadTemplate,
  parseExcelFile,
  importCaregivers,
} from "../../../../utils/importCaregivers";
import Step2ServiceDetails from "./Step2ServiceDetails";

const LANGUAGES = ["Amharic", "English", "Afaan Oromoo", "Tigrigna", "Somali"];
const LANGUAGES_AMH = ["አማርኛ", "እንግሊዝኛ", "አፋን ኦሮሞ", "ትግርኛ", "ኦሮምኛ", "ሶማሊኛ"];
const AGE_GROUPS = ["NEW_BORN", "TODDLER", "SCHOOL_AGE", "ALL_AGES"];
const AGE_GROUP_DISPLAY = {
  NEW_BORN: "New Born",
  TODDLER: "Toddler",
  SCHOOL_AGE: "School Age",
  ALL_AGES: "All Ages",
};
const SUBCITIES = [
  "Addis Ketema",
  "Akaki Kaliti",
  "Arada",
  "Bole",
  "Gullele",
  "Kirkos",
  "Kolfe Keranio",
  "Lideta",
  "Nifas Silk-Lafto",
  "Yeka",
  "Lemi Kura",
];
const TEACHING_LEVELS = [
  "Elementary",
  "Middle School",
  "High School",
  "Preparatory",
  "University/College",
];
const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const HOURS_PER_DAY = ["1-2", "3-5", "more than 5"];
const FAMILY_SIZES = ["small", "medium", "large"];
const PREFERRED_WORK_HOURS = ["morning", "afternoon", "evening"];
const EDUCATION_LEVELS = [
  "Below 6th Grade",
  "6th Grade",
  "7th Grade",
  "8th Grade",
  "9th Grade",
  "10th Grade",
  "11th Grade",
  "12th Grade",
  "TVET Program",
  "Diploma",
  "Bachelor's Degree",
  "Master's Degree",
];

const AddCaregiver = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const {
    backendUrl,
    aToken,
    fetchCaregiverById,
    serviceCategories,
    fetchCategoryFields,
    createCaregiver,
    updateCaregiverForEdit,
  } = useContext(AdminContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  // EAV dynamic fields state
  const [fieldValues, setFieldValues] = useState({}); // { fieldId: [values] }
  const [categoryFields, setCategoryFields] = useState([]); // CategoryField[] for selected category

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [importStep, setImportStep] = useState(1);

  // Loading state for edit mode
  const [loading, setLoading] = useState(isEditMode);

  // Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    nameAmh: "",
    email: "",
    phone: "",
    alternativePhone: "",
    password: "",
    serviceType: "",
    // Step 2: Dynamic
    childcareAgeGroups: [],
    dishes: [],
    tutorSubjects: "",
    subjectsAmh: "",
    tutorGrades: "",
    major: "",
    majorAmh: "",
    hobbies: "",
    hobbiesAmh: "",
    teachingLevels: [],
    preferredDays: [],
    hoursPerDay: "",
    elderlyExperience: "",
    elderlyCareFields: {
      elderly_care_experience: "No",
      first_aid_cpr_certified: "No",
      transportation_assistance: "No",
      meal_preparation: "No",
      light_housekeeping: "No",
      medication_management: "No",
      mobility_assistance: "No",
      companionship: "No",
    },
    serviceFrequency: "",
    preferredWorkHours: "",
    familySize: "",
    // Step 3: Personal
    gender: "Female",
    dob: "",
    rating: 3,
    nationality: "Ethiopian",
    nationalityAmh: "ኢትዮጵያዊ",
    languages: [],
    languagesAmh: [],
    subcity: "",
    // Address fields as text inputs
    addressLine1: "",
    addressLine2: "",
    addressLine1Amh: "",
    addressLine2Amh: "",
    educationLevel: "",
    educationField: "",
    educationAmh: "",
    about: "",
    aboutAmh: "",
    courseDetails: "",
    courseDetailsAmh: "",
    hasCourse: false,
    refereeName: "",
    refereeNameAmh: "",
    refereePhone: "",
    hasReferee: false,
    // Additional
    experience: 1,
    salary: 4500,
    dateTimePreference: "part-time",
    isAvailable: false,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Load caregiver data for edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadCaregiver = async () => {
        try {
          const caregiverData = await fetchCaregiverById(id);
          if (caregiverData) {
            // Parse name into first and last name
            const nameParts = (caregiverData.name || "").split(" ");
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            // Parse languages
            let languages = [];
            let languagesAmh = [];
            if (caregiverData.languages) {
              if (typeof caregiverData.languages === "string") {
                try {
                  languages = JSON.parse(caregiverData.languages);
                } catch {
                  languages = caregiverData.languages
                    .split(",")
                    .map((l) => l.trim())
                    .filter((l) => l);
                }
              } else if (Array.isArray(caregiverData.languages)) {
                languages = caregiverData.languages;
              }
            }

            // Parse address
            let addressLine1 = "";
            let addressLine2 = "";
            let addressLine1Amh = "";
            let addressLine2Amh = "";
            if (caregiverData.address) {
              try {
                const address =
                  typeof caregiverData.address === "string"
                    ? JSON.parse(caregiverData.address)
                    : caregiverData.address;
                addressLine1 = address.line1 || "";
                addressLine2 = address.line2 || "";
              } catch {
                // If address is just a string
                addressLine1 = caregiverData.address;
              }
            }
            addressLine1 = caregiverData.addressLine1 || addressLine1;
            addressLine2 = caregiverData.addressLine2 || addressLine2;

            if (caregiverData.address_amh) {
              try {
                const addressAmh =
                  typeof caregiverData.address_amh === "string"
                    ? JSON.parse(caregiverData.address_amh)
                    : caregiverData.address_amh;
                addressLine1Amh = addressAmh.line1_amh || "";
                addressLine2Amh = addressAmh.line2_amh || "";
              } catch {
                addressLine1Amh = caregiverData.address_amh;
              }
            }
            addressLine1Amh = caregiverData.addressLine1_amh || addressLine1Amh;
            addressLine2Amh = caregiverData.addressLine2_amh || addressLine2Amh;

            // Parse education
            let educationLevel = "";
            let educationField = "";
            if (caregiverData.education) {
              const educationParts = caregiverData.education.split(" in ");
              educationLevel = educationParts[0] || "";
              educationField = educationParts.slice(1).join(" ") || "";
            }

            // Determine service type from category - use the label directly from serviceCategory
            const serviceType = caregiverData.serviceCategory?.label || "";

            // Parse childcare age groups
            let childcareAgeGroups = [];
            if (caregiverData.age_groups) {
              try {
                childcareAgeGroups =
                  typeof caregiverData.age_groups === "string"
                    ? JSON.parse(caregiverData.age_groups)
                    : caregiverData.age_groups;
              } catch {
                childcareAgeGroups = [];
              }
            }

            // Parse dishes
            let dishes = [];
            if (caregiverData.dishes) {
              try {
                const dishesObj =
                  typeof caregiverData.dishes === "string"
                    ? JSON.parse(caregiverData.dishes)
                    : caregiverData.dishes;
                dishes = [dishesObj];
              } catch {
                dishes = [];
              }
            }

            // Parse teaching levels
            let teachingLevels = [];
            if (caregiverData.teaching_levels) {
              try {
                teachingLevels =
                  typeof caregiverData.teaching_levels === "string"
                    ? JSON.parse(caregiverData.teaching_levels)
                    : caregiverData.teaching_levels;
              } catch {
                teachingLevels = [];
              }
            }

            // Parse preferred days
            let preferredDays = [];
            if (caregiverData.preferred_days) {
              try {
                preferredDays =
                  typeof caregiverData.preferred_days === "string"
                    ? JSON.parse(caregiverData.preferred_days)
                    : caregiverData.preferred_days;
              } catch {
                preferredDays = [];
              }
            }

            // Parse elderly care fields
            const elderlyCareFields = {
              elderly_care_experience:
                caregiverData.elderly_care_experience || "No",
              first_aid_cpr_certified:
                caregiverData.first_aid_cpr_certified || "No",
              transportation_assistance:
                caregiverData.transportation_assistance || "No",
              meal_preparation: caregiverData.meal_preparation || "No",
              light_housekeeping: caregiverData.light_housekeeping || "No",
              medication_management:
                caregiverData.medication_management || "No",
              mobility_assistance: caregiverData.mobility_assistance || "No",
              companionship: caregiverData.companionship || "No",
            };

            // Set avatar preview
            if (caregiverData.image) {
              setAvatarPreview(caregiverData.image);
            }

            // Format DOB for date input (needs YYYY-MM-DD format)
            let formattedDob = "";
            if (caregiverData.dob) {
              const dobValue = caregiverData.dob;
              if (typeof dobValue === "string" && dobValue.includes("T")) {
                formattedDob = dobValue.split("T")[0];
              } else if (typeof dobValue === "string") {
                formattedDob = dobValue;
              } else if (dobValue instanceof Date) {
                formattedDob = dobValue.toISOString().split("T")[0];
              }
            }

            // Set form data
            setFormData({
              firstName,
              lastName,
              nameAmh: caregiverData.nameAmh || caregiverData.name_amh || "",
              email: caregiverData.email || "",
              phone: caregiverData.phoneNumber || "",
              alternativePhone:
                caregiverData.alternativePhone ||
                caregiverData.alternativePhoneNumber ||
                "",
              password: "", // Don't load password for security
              serviceType,
              childcareAgeGroups,
              dishes,
              tutorSubjects: caregiverData.subjects || "",
              subjectsAmh: caregiverData.subjects_amh || "",
              tutorGrades: "",
              major: caregiverData.major || "",
              majorAmh: caregiverData.major_amh || "",
              hobbies: caregiverData.hobbies || "",
              hobbiesAmh: caregiverData.hobbies_amh || "",
              teachingLevels,
              preferredDays,
              hoursPerDay: caregiverData.hours_per_day || "",
              elderlyExperience: "",
              elderlyCareFields,
              serviceFrequency: caregiverData.service_frequency || "",
              preferredWorkHours: caregiverData.preferred_work_hours || "",
              familySize: caregiverData.family_size || "",
              gender: caregiverData.gender || "Female",
              dob: formattedDob,
              rating: caregiverData.rating || 3,
              nationality: caregiverData.nationality || "Ethiopian",
              nationalityAmh: caregiverData.nationality_amh || "ኢትዮጵያዊ",
              languages: languages || [],
              languagesAmh: caregiverData.languages_amh
                ? typeof caregiverData.languages_amh === "string"
                  ? JSON.parse(caregiverData.languages_amh)
                  : caregiverData.languages_amh
                : [],
              subcity: caregiverData.subcity || "",
              addressLine1,
              addressLine2,
              addressLine1Amh,
              addressLine2Amh,
              educationLevel,
              educationField,
              educationAmh: caregiverData.education_amh || "",
              about: caregiverData.about || "",
              aboutAmh: caregiverData.aboutAmh || caregiverData.about_amh || "",
              courseDetails: caregiverData.courseDetails || "",
              courseDetailsAmh: caregiverData.courseDetails_amh || "",
              hasCourse: caregiverData.hasCourse || false,
              refereeName: caregiverData.refereeName || "",
              refereeNameAmh: caregiverData.refereeName_amh || "",
              refereePhone:
                caregiverData.refereePhone ||
                caregiverData.refereePhoneNumber ||
                "",
              hasReferee: caregiverData.hasReferee || false,
              // Keep zero as a valid experience value when editing.
              experience: caregiverData.experience ?? 1,
              salary: caregiverData.fees || 4500,
              dateTimePreference:
                caregiverData.workTimePreference?.replace(/_/g, "-") ||
                caregiverData.date_time_preference_to_work ||
                "part-time",
              isAvailable: caregiverData.available || false,
            });

            if (
              caregiverData.fieldValues &&
              Array.isArray(caregiverData.fieldValues)
            ) {
              const loadedFieldValues = {};
              caregiverData.fieldValues.forEach((fieldValue) => {
                const rawValues = fieldValue.values;
                if (Array.isArray(rawValues)) {
                  loadedFieldValues[fieldValue.fieldId] = rawValues.map((v) =>
                    String(v),
                  );
                } else if (rawValues === null || rawValues === undefined) {
                  loadedFieldValues[fieldValue.fieldId] = [];
                } else if (typeof rawValues === "string") {
                  try {
                    const parsed = JSON.parse(rawValues);
                    loadedFieldValues[fieldValue.fieldId] = Array.isArray(
                      parsed,
                    )
                      ? parsed.map((v) => String(v))
                      : [rawValues];
                  } catch {
                    loadedFieldValues[fieldValue.fieldId] = [rawValues];
                  }
                } else {
                  loadedFieldValues[fieldValue.fieldId] = [String(rawValues)];
                }
              });
              setFieldValues(loadedFieldValues);
            }
          }
        } catch (error) {
          toast.error("Failed to load caregiver data");
          navigate("/caregivers/all");
        } finally {
          setLoading(false);
        }
      };

      loadCaregiver();
    }
  }, [id, isEditMode, fetchCaregiverById, navigate]);

  // Fetch category fields whenever serviceType changes
  useEffect(() => {
    if (!formData.serviceType) {
      setCategoryFields([]);
      setFieldValues({});
      return;
    }
    const cat = serviceCategories.find(
      (c) =>
        c.label === formData.serviceType || c.name === formData.serviceType,
    );
    if (!cat) return;
    const fetchFields = async () => {
      try {
        const fields = await fetchCategoryFields(cat.id);
        setCategoryFields(fields);
      } catch {
        setCategoryFields([]);
      }
    };
    fetchFields();
  }, [formData.serviceType, serviceCategories, fetchCategoryFields]);

  // Import functions
  const handleDownloadTemplate = () => {
    try {
      downloadTemplate();
      toast.success("Template downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download template: " + error.message);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "application/csv",
    ];
    const validExtensions = [".xlsx", ".csv"];
    const fileName = file.name.toLowerCase();

    if (
      !validTypes.includes(file.type) &&
      !validExtensions.some((ext) => fileName.endsWith(ext))
    ) {
      toast.error("Please upload a valid import file (.xlsx or .csv)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    setImportFile(file);
    setImportStep(2);
    processExcelFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
        "application/csv",
      ];
      const validExtensions = [".xlsx", ".csv"];
      const fileName = file.name.toLowerCase();
      if (
        validTypes.includes(file.type) ||
        validExtensions.some((ext) => fileName.endsWith(ext))
      ) {
        setImportFile(file);
        setImportStep(2);
        processExcelFile(file);
      } else {
        toast.error("Please upload a valid import file (.xlsx or .csv)");
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const processExcelFile = async (file) => {
    try {
      const { validatedData, errors } = await parseExcelFile(file);
      setImportErrors(errors);

      if (errors.length > 0) {
        toast.error(`Found ${errors.length} validation errors in the file`);
      } else {
        toast.success(`Successfully parsed ${validatedData.length} caregivers`);
      }
    } catch (error) {
      toast.error(`Error processing file: ${error.message}`);
      setImportStep(1);
      setImportFile(null);
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      toast.error("Please select a file first");
      return;
    }

    setIsImporting(true);
    setImportProgress(0);

    try {
      const { validatedData, errors } = await parseExcelFile(importFile);

      if (errors.length > 0) {
        toast.error(`Cannot import: ${errors.length} validation errors found`);
        setIsImporting(false);
        return;
      }

      if (validatedData.length === 0) {
        toast.error("No valid data found in the file");
        setIsImporting(false);
        return;
      }

      if (validatedData.length > 300) {
        if (
          !window.confirm(
            `You are about to import ${validatedData.length} caregivers. This might take a moment. Continue?`,
          )
        ) {
          setIsImporting(false);
          return;
        }
      }

      const results = await importCaregivers(
        importFile,
        backendUrl,
        aToken,
        (progress) => setImportProgress(progress),
      );

      setImportResults(results);
      setImportStep(3);

      if (results.success > 0) {
        toast.success(`Successfully imported ${results.success} caregivers`);
      }

      if (results.failed > 0) {
        toast.error(`${results.failed} caregivers failed to import`);
        if (results.errors.length > 0) {
          console.error("Import errors:", results.errors);
        }
      }
    } catch (error) {
      toast.error(`Import failed: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportErrors([]);
    setImportResults(null);
    setImportStep(1);
    setImportProgress(0);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    resetImport();
  };

  // Helper to scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Validation functions
  const validateNumberInput = (value, fieldName) => {
    if (value === "") return "";
    if (!/^\d+$/.test(value)) {
      return `${fieldName} must contain only numbers`;
    }
    return "";
  };

  const validateServiceFrequency = (value) => {
    if (value === "") return "";
    if (!/^\d+$/.test(value)) {
      return "Service frequency must be a number";
    }
    const num = parseInt(value);
    if (num < 1 || num > 7) {
      return "Service frequency must be between 1 and 7 days";
    }
    return "";
  };

  const validateSalary = (value) => {
    if (value === "") return "";
    if (!/^\d+$/.test(value)) {
      return "Salary must be a number";
    }
    const num = parseInt(value);
    if (num < 0) {
      return "Salary cannot be negative";
    }
    return "";
  };

  const validateExperience = (value) => {
    if (value === "") return "";
    if (value === "below_1") return "";
    if (!/^\d+$/.test(value)) {
      return "Experience must be a number";
    }
    const num = parseInt(value);
    if (num < 0 || num > 20) {
      return "Experience must be between 0 and 20 years";
    }
    return "";
  };

  const validatePhone = (value) => {
    if (!value) return "Phone number is required";
    if (!/^\d+$/.test(value)) {
      return "Phone number must contain only numbers";
    }
    if (value.length < 10) {
      return "Phone number must be at least 10 digits";
    }
    return "";
  };

  const validateAlternativePhone = (value) => {
    if (!value) return "";
    if (!/^\d+$/.test(value)) {
      return "Alternative phone must contain only numbers";
    }
    if (value.length < 10) {
      return "Alternative phone must be at least 10 digits";
    }
    return "";
  };

  const validatePassword = (value) => {
    if (!isEditMode && !value) return "Password is required";
    if (value && value.length < 6) {
      return "Password must be at least 6 characters";
    }
    return "";
  };

  const validateEmail = (value) => {
    // Email is optional, so if empty, no error
    if (!value || value.trim() === "") return "";
    // If provided, validate format
    if (!/\S+@\S+\.\S/.test(value)) {
      return "Email is invalid";
    }
    return "";
  };

  const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === "") {
      return `${fieldName} is required`;
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let error = "";

    switch (name) {
      case "phone":
        error = validatePhone(value);
        break;
      case "alternativePhone":
        error = validateAlternativePhone(value);
        break;
      case "password":
        error = validatePassword(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "salary":
        error = validateSalary(value);
        break;
      case "experience":
        error = validateExperience(value);
        break;
      case "serviceFrequency":
        error = validateServiceFrequency(value);
        break;
      case "firstName":
      case "lastName":
      case "serviceType":
        error = validateRequired(value, name);
        break;
      default:
        break;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));

    if (name === "serviceType") {
      setFieldValues({});
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    } else if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Multi-select helpers
  const toggleLanguage = (lang) => {
    setFormData((prev) => {
      const langs = prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang];
      return { ...prev, languages: langs };
    });
  };

  const toggleLanguageAmh = (lang) => {
    setFormData((prev) => {
      const langs = prev.languagesAmh.includes(lang)
        ? prev.languagesAmh.filter((l) => l !== lang)
        : [...prev.languagesAmh, lang];
      return { ...prev, languagesAmh: langs };
    });
  };

  const toggleAgeGroup = (group) => {
    setFormData((prev) => {
      const groups = prev.childcareAgeGroups.includes(group)
        ? prev.childcareAgeGroups.filter((g) => g !== group)
        : [...prev.childcareAgeGroups, group];
      return { ...prev, childcareAgeGroups: groups };
    });
  };

  // Dish helpers
  const addDish = () => {
    setFormData((prev) => ({
      ...prev,
      dishes: [
        ...prev.dishes,
        {
          lunch: "",
          lunch_amh: "",
          habesha_lunch: "",
          habesha_lunch_amh: "",
          breakfast: "",
          breakfast_amh: "",
          habesha_dinner: "",
          habesha_dinner_amh: "",
          habesha_breakfast: "",
          habesha_breakfast_amh: "",
          dinner: "",
          dinner_amh: "",
        },
      ],
    }));
  };

  const updateDish = (index, field, value) => {
    const newDishes = [...formData.dishes];
    newDishes[index][field] = value;
    setFormData((prev) => ({ ...prev, dishes: newDishes }));
  };

  const removeDish = (index) => {
    setFormData((prev) => ({
      ...prev,
      dishes: prev.dishes.filter((_, i) => i !== index),
    }));
  };

  const handleMultipleSelectChange = (name) => (e) => {
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      (option) => option.value,
    );
    setFormData((prev) => ({ ...prev, [name]: selectedOptions }));
  };

  const handleElderlyFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      elderlyCareFields: { ...prev.elderlyCareFields, [field]: value },
    }));
  };

  // Validation for each step
  const validateStep1 = () => {
    const newErrors = {};

    newErrors.firstName = validateRequired(formData.firstName, "First name");
    newErrors.lastName = validateRequired(formData.lastName, "Last name");
    newErrors.email = validateEmail(formData.email);
    newErrors.phone = validatePhone(formData.phone);
    newErrors.alternativePhone = validateAlternativePhone(
      formData.alternativePhone,
    );
    newErrors.password = validatePassword(formData.password);
    newErrors.serviceType = validateRequired(
      formData.serviceType,
      "Service type",
    );
    newErrors.experience = validateExperience(formData.experience.toString());
    newErrors.salary = validateSalary(formData.salary.toString());

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, value]) => value !== ""),
    );

    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    // Validate dynamic EAV fields
    categoryFields.forEach((field) => {
      if (field.isRequired) {
        const values = fieldValues[field.id] || [];
        const hasValue =
          values.length > 0 && values.some((v) => v && v.trim() !== "");
        if (!hasValue) {
          newErrors[field.id] = `${field.label} is required`;
        }
      }
    });

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, value]) => value !== ""),
    );

    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = "Address line 1 is required";
    }

    if (!formData.subcity) {
      newErrors.subcity = "Subcity is required";
    }

    if (!formData.educationLevel) {
      newErrors.educationLevel = "Education level is required";
    }

    // Validate required fields for Tutor
    if (formData.serviceType === "Tutor") {
      if (!formData.educationLevel) {
        newErrors.educationLevel = "Education level is required for tutors";
      }

      // Validate that educationLevel is one of the allowed values
      if (
        formData.educationLevel &&
        !EDUCATION_LEVELS.includes(formData.educationLevel)
      ) {
        newErrors.educationLevel = `Education level must be one of: ${EDUCATION_LEVELS.join(
          ", ",
        )}`;
      }
    }

    // Validate languages if required (at least one)
    if (formData.languages.length === 0) {
      newErrors.languages = "At least one language is required";
    }

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, value]) => value !== ""),
    );

    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const handleNext = () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    }

    if (isValid) {
      setCurrentStep((prev) => prev + 1);
      scrollToTop();
    } else {
      toast.error("Please fix the validation errors before proceeding");
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
    scrollToTop();
  };

  const handleSubmit = async () => {
    // Validate all steps before submission
    if (!validateStep1() || !validateStep2() || !validateStep3()) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    setSubmitting(true);

    try {
      const categoryMap = {
        Childcare: "childcare",
        Cook: "cook",
        Tutor: "homeTutor",
        "Elderly Care": "elderlycare",
      };
      const category = categoryMap[formData.serviceType];

      const prepareDishesObject = () => {
        if (formData.dishes.length === 0) return {};

        const dish = formData.dishes[0];
        return {
          lunch: dish.lunch || "",
          lunch_amh: dish.lunch_amh || "",
          habesha_lunch: dish.habesha_lunch || "",
          habesha_lunch_amh: dish.habesha_lunch_amh || "",
          breakfast: dish.breakfast || "",
          breakfast_amh: dish.breakfast_amh || "",
          habesha_dinner: dish.habesha_dinner || "",
          habesha_dinner_amh: dish.habesha_dinner_amh || "",
          habesha_breakfast: dish.habesha_breakfast || "",
          habesha_breakfast_amh: dish.habesha_breakfast_amh || "",
          dinner: dish.dinner || "",
          dinner_amh: dish.dinner_amh || "",
        };
      };

      const fd = new FormData();

      if (avatarFile) {
        fd.append("image", avatarFile);
      }

      // Basic information
      fd.append("name", `${formData.firstName} ${formData.lastName}`);
      fd.append("name_amh", formData.nameAmh || "");
      fd.append("email", formData.email || "");
      if (formData.password) {
        fd.append("password", formData.password);
      }
      fd.append("phoneNumber", formData.phone);
      fd.append("alternativePhoneNumber", formData.alternativePhone || "");
      fd.append("category", category);
      fd.append(
        "education",
        formData.educationField
          ? `${formData.educationLevel} in ${formData.educationField}`.trim()
          : formData.educationLevel || "",
      );
      fd.append("education_amh", formData.educationAmh || "አልተገለጸም");
      fd.append(
        "experience",
        (formData.experience === "below_1"
          ? 0
          : formData.experience
        ).toString(),
      );
      fd.append("nationality", formData.nationality || "Not specified");
      fd.append("nationality_amh", formData.nationalityAmh || "አልተገለጸም");
      fd.append("languages", JSON.stringify(formData.languages));
      fd.append("languages_amh", JSON.stringify(formData.languagesAmh));
      fd.append("date_time_preference_to_work", formData.dateTimePreference);
      fd.append("about", formData.about || "");
      fd.append("about_amh", formData.aboutAmh || "");
      fd.append("gender", formData.gender);
      fd.append("fees", formData.salary.toString());
      fd.append("rating", formData.rating.toString());
      fd.append("available", formData.isAvailable.toString());

      // Address as JSON object
      fd.append(
        "address",
        JSON.stringify({
          line1: formData.addressLine1 || "",
          line2: formData.addressLine2 || "",
        }),
      );

      // Address Amharic as JSON object
      fd.append(
        "address_amh",
        JSON.stringify({
          line1_amh: formData.addressLine1Amh || "",
          line2_amh: formData.addressLine2Amh || "",
        }),
      );

      fd.append("dob", formData.dob || "");
      fd.append("hasCourse", formData.hasCourse.toString());
      fd.append(
        "courseDetails",
        formData.hasCourse ? formData.courseDetails || "" : "",
      );
      fd.append(
        "courseDetails_amh",
        formData.hasCourse ? formData.courseDetailsAmh || "" : "",
      );
      fd.append("hasReferee", formData.hasReferee.toString());
      fd.append(
        "refereeName",
        formData.hasReferee ? formData.refereeName || "" : "",
      );
      fd.append(
        "refereeName_amh",
        formData.hasReferee ? formData.refereeNameAmh || "" : "",
      );
      fd.append(
        "refereePhoneNumber",
        formData.hasReferee ? formData.refereePhone || "" : "",
      );
      fd.append("subcity", formData.subcity);

      // Append EAV dynamic field values
      const eavValues = Object.entries(fieldValues).map(
        ([fieldId, values]) => ({
          fieldId,
          values,
        }),
      );
      if (eavValues.length > 0) {
        fd.append("fieldValues", JSON.stringify(eavValues));
      }

      // Find the selected category id
      const selectedCat = serviceCategories.find(
        (c) =>
          c.label === formData.serviceType || c.name === formData.serviceType,
      );
      if (selectedCat) {
        fd.append("serviceCategoryId", selectedCat.id);
        fd.append("category", selectedCat.name);
      }

      let response;
      if (isEditMode) {
        // Update existing caregiver
        response = await updateCaregiverForEdit(id, fd);
      } else {
        // Create new caregiver
        response = await createCaregiver(fd);
      }

      if (response.success) {
        if (!isEditMode) {
          // Reset form for add mode
          setAvatarFile(null);
          setAvatarPreview(null);
          setFormData({
            firstName: "",
            lastName: "",
            nameAmh: "",
            email: "",
            phone: "",
            alternativePhone: "",
            password: "",
            serviceType: "",
            childcareAgeGroups: [],
            dishes: [],
            tutorSubjects: "",
            subjectsAmh: "",
            tutorGrades: "",
            major: "",
            majorAmh: "",
            hobbies: "",
            hobbiesAmh: "",
            teachingLevels: [],
            preferredDays: [],
            hoursPerDay: "",
            elderlyExperience: "",
            elderlyCareFields: {
              elderly_care_experience: "No",
              first_aid_cpr_certified: "No",
              transportation_assistance: "No",
              meal_preparation: "No",
              light_housekeeping: "No",
              medication_management: "No",
              mobility_assistance: "No",
              companionship: "No",
            },
            serviceFrequency: "",
            preferredWorkHours: "",
            familySize: "",
            gender: "Female",
            dob: "",
            rating: 3,
            nationality: "Ethiopian",
            nationalityAmh: "ኢትዮጵያዊ",
            languages: [],
            languagesAmh: [],
            subcity: "",
            addressLine1: "",
            addressLine2: "",
            addressLine1Amh: "",
            addressLine2Amh: "",
            educationLevel: "",
            educationField: "",
            educationAmh: "",
            about: "",
            aboutAmh: "",
            courseDetails: "",
            courseDetailsAmh: "",
            hasCourse: false,
            refereeName: "",
            refereeNameAmh: "",
            refereePhone: "",
            hasReferee: false,
            experience: 1,
            salary: 4500,
            dateTimePreference: "part-time",
            isAvailable: false,
          });
          setCurrentStep(1);
        } else {
          // Navigate back to caregivers list after edit
          setTimeout(() => {
            navigate("/caregivers/all");
          }, 1000);
        }
        setErrors({});
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          "Error submitting form: " + (error.message || "Unknown error"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-gray-500">Loading caregiver data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto pb-12">
      {/* Top Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/caregivers/all")}
            className="p-2 rounded-xl hover:bg-accent dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={24} className="text-text dark:text-dark-text" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-text dark:text-dark-text">
              {isEditMode ? "Edit Caregiver" : "Add New Caregiver"}
            </h1>
            <p className="text-gray-500 dark:text-dark-muted mt-1">
              {isEditMode
                ? "Update caregiver information"
                : "Register a new caregiver to the platform"}
            </p>
          </div>
        </div>
        {!isEditMode && (
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-card dark:bg-dark-surface border border-accent dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:bg-accent dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            <Upload size={18} /> Import Caregivers
          </button>
        )}
      </div>
      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-accent dark:border-dark-border flex justify-between items-center">
              <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
                <FileSpreadsheet size={20} className="text-primary" /> Import
                Caregivers (Minimal)
              </h3>
              <button
                onClick={closeImportModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text p-1 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 flex-1 overflow-y-auto">
              {importStep === 1 && (
                <div className="space-y-6">
                  {/* Information Banner */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Info
                        size={20}
                        className="text-blue-600 dark:text-blue-400 mt-0.5"
                      />
                      <div>
                        <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">
                          Minimal Import Format
                        </h4>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          Required columns are name, phoneNumber, categorySlug,
                          and experience. Password will be auto-generated from
                          phone number.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Step 1: Download Template
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-dark-muted mb-4">
                      Download the Excel template with the caregiver import
                      columns, including categorySlug for service category.
                    </p>
                    <button
                      onClick={handleDownloadTemplate}
                      className="flex items-center gap-2 px-4 py-2.5 bg-primary/10 text-primary rounded-xl font-medium hover:bg-primary/20 transition-colors"
                    >
                      <Download size={16} /> Get Template
                    </button>

                    {/* Template Preview */}
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Template Columns:
                      </p>
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium">name</span>
                        <span className="font-medium">name_amh</span>
                        <span className="font-medium">phoneNumber</span>
                        <span className="font-medium">alternativePhone</span>
                        <span className="font-medium">categorySlug</span>
                        <span className="font-medium">experience</span>
                        <span className="font-medium">fees</span>
                        <span className="font-medium">languages</span>
                        <span className="font-medium">subcity</span>
                        <span className="font-medium">education_amh</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Step 2: Upload Filled Template
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-dark-muted mb-4">
                      Upload your filled Excel file. We&apos;ll validate the
                      data before importing.
                    </p>

                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                        importFile
                          ? "border-primary bg-primary/5"
                          : "border-accent hover:border-primary"
                      }`}
                    >
                      <FileSpreadsheet
                        size={48}
                        className="mx-auto mb-4 text-primary"
                      />
                      <p className="text-sm text-gray-600 dark:text-dark-muted mb-2">
                        Drag & drop your Excel file here, or
                      </p>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".xlsx,.csv"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors">
                          <Upload size={16} /> Select File
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-dark-muted mt-4">
                        Supported formats: .xlsx, .csv (Max 5MB)
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                        Max 300 caregivers per import
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {importStep === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted">
                      File Validation
                    </h4>
                    <button
                      onClick={resetImport}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-muted dark:hover:text-dark-text"
                    >
                      Change File
                    </button>
                  </div>

                  <div className="bg-accent dark:bg-gray-900/50 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <FileSpreadsheet size={24} className="text-primary" />
                      <div>
                        <p className="font-medium text-text dark:text-dark-text">
                          {importFile.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          {(importFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  </div>

                  {importErrors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <h5 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
                        <AlertCircle size={16} /> Validation Errors (
                        {importErrors.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto">
                        {importErrors.slice(0, 5).map((error, index) => (
                          <p
                            key={index}
                            className="text-sm text-red-600 dark:text-red-300 mb-1"
                          >
                            • {error}
                          </p>
                        ))}
                        {importErrors.length > 5 && (
                          <p className="text-sm text-red-600 dark:text-red-300">
                            ... and {importErrors.length - 5} more errors
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                        Please fix these errors in your Excel file before
                        importing.
                      </p>
                    </div>
                  )}

                  {importErrors.length === 0 && importFile && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle
                          size={20}
                          className="text-green-600 dark:text-green-400"
                        />
                        <h5 className="text-sm font-semibold text-green-700 dark:text-green-300">
                          File validation successful!
                        </h5>
                      </div>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        Your Excel file has passed all validation checks. Ready
                        to import.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setImportStep(1)}
                      className="px-4 py-2 text-gray-600 dark:text-dark-muted hover:text-gray-800 dark:hover:text-dark-text font-medium"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleImportSubmit}
                      disabled={importErrors.length > 0 || isImporting}
                      className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isImporting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />{" "}
                          Importing...
                        </>
                      ) : (
                        "Start Import"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {importStep === 3 && importResults && (
                <div className="space-y-6">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted">
                    Import Results
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                        {importResults.success}
                      </p>
                      <p className="text-sm text-emerald-600 dark:text-emerald-300">
                        Successful
                      </p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                        {importResults.failed}
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-300">
                        Failed
                      </p>
                    </div>
                  </div>

                  {importResults.success > 0 && (
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle
                          size={18}
                          className="text-emerald-600 dark:text-emerald-400"
                        />
                        <h5 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                          Import Successful
                        </h5>
                      </div>
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">
                        {importResults.success} caregivers have been imported
                        successfully. You can edit their additional details in
                        the admin panel.
                      </p>
                    </div>
                  )}

                  {importResults.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <h5 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
                        Failed Imports ({importResults.errors.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto">
                        {importResults.errors
                          .slice(0, 3)
                          .map((error, index) => (
                            <div key={index} className="mb-2">
                              <p className="text-sm font-medium text-red-600 dark:text-red-300">
                                {error.row
                                  ? `Row ${error.row}`
                                  : error.phone
                                    ? `Phone: ${error.phone}`
                                    : "Error"}
                              </p>
                              <p className="text-xs text-red-500 dark:text-red-400">
                                {error.error}
                              </p>
                            </div>
                          ))}
                        {importResults.errors.length > 3 && (
                          <p className="text-sm text-red-600 dark:text-red-300">
                            ... and {importResults.errors.length - 3} more
                            errors
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                        These caregivers were not imported. Please check the
                        errors and try again.
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={closeImportModal}
                      className="px-4 py-2 text-gray-600 dark:text-dark-muted hover:text-gray-800 dark:hover:text-dark-text font-medium"
                    >
                      Close
                    </button>
                    <button
                      onClick={resetImport}
                      className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                    >
                      Import More
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {isImporting && (
              <div className="px-6 py-3 border-t border-accent dark:border-dark-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-dark-muted">
                    Importing caregivers...
                  </span>
                  <span className="text-sm font-medium text-text dark:text-dark-text">
                    {importProgress}%
                  </span>
                </div>
                <div className="h-2 bg-accent dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  {importProgress < 100
                    ? "Processing... This may take a moment."
                    : "Import completed!"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}{" "}
      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-accent dark:border-dark-border flex justify-between items-center">
              <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
                <FileSpreadsheet size={20} className="text-primary" /> Import
                Caregivers (Minimal)
              </h3>
              <button
                onClick={closeImportModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text p-1 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 flex-1 overflow-y-auto">
              {importStep === 1 && (
                <div className="space-y-6">
                  {/* Information Banner */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Info
                        size={20}
                        className="text-blue-600 dark:text-blue-400 mt-0.5"
                      />
                      <div>
                        <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">
                          Minimal Import Format
                        </h4>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          Required columns are name, phoneNumber, categorySlug,
                          and experience. Password will be auto-generated from
                          phone number.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Step 1: Download Template
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-dark-muted mb-4">
                      Download the Excel template with the caregiver import
                      columns, including categorySlug for service category.
                    </p>
                    <button
                      onClick={handleDownloadTemplate}
                      className="flex items-center gap-2 px-4 py-2.5 bg-primary/10 text-primary rounded-xl font-medium hover:bg-primary/20 transition-colors"
                    >
                      <Download size={16} /> Get Template
                    </button>

                    {/* Template Preview */}
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Template Columns:
                      </p>
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium">name</span>
                        <span className="font-medium">name_amh</span>
                        <span className="font-medium">phoneNumber</span>
                        <span className="font-medium">alternativePhone</span>
                        <span className="font-medium">categorySlug</span>
                        <span className="font-medium">experience</span>
                        <span className="font-medium">fees</span>
                        <span className="font-medium">languages</span>
                        <span className="font-medium">subcity</span>
                        <span className="font-medium">education_amh</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Step 2: Upload Filled Template
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-dark-muted mb-4">
                      Upload your filled Excel file. We&apos;ll validate the
                      data before importing.
                    </p>

                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                        importFile
                          ? "border-primary bg-primary/5"
                          : "border-accent hover:border-primary"
                      }`}
                    >
                      <FileSpreadsheet
                        size={48}
                        className="mx-auto mb-4 text-primary"
                      />
                      <p className="text-sm text-gray-600 dark:text-dark-muted mb-2">
                        Drag & drop your Excel file here, or
                      </p>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".xlsx,.csv"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors">
                          <Upload size={16} /> Select File
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-dark-muted mt-4">
                        Supported formats: .xlsx, .csv (Max 5MB)
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                        Max 300 caregivers per import
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {importStep === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted">
                      File Validation
                    </h4>
                    <button
                      onClick={resetImport}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-muted dark:hover:text-dark-text"
                    >
                      Change File
                    </button>
                  </div>

                  <div className="bg-accent dark:bg-gray-900/50 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <FileSpreadsheet size={24} className="text-primary" />
                      <div>
                        <p className="font-medium text-text dark:text-dark-text">
                          {importFile.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          {(importFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  </div>

                  {importErrors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <h5 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
                        <AlertCircle size={16} /> Validation Errors (
                        {importErrors.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto">
                        {importErrors.slice(0, 5).map((error, index) => (
                          <p
                            key={index}
                            className="text-sm text-red-600 dark:text-red-300 mb-1"
                          >
                            • {error}
                          </p>
                        ))}
                        {importErrors.length > 5 && (
                          <p className="text-sm text-red-600 dark:text-red-300">
                            ... and {importErrors.length - 5} more errors
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                        Please fix these errors in your Excel file before
                        importing.
                      </p>
                    </div>
                  )}

                  {importErrors.length === 0 && importFile && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle
                          size={20}
                          className="text-green-600 dark:text-green-400"
                        />
                        <h5 className="text-sm font-semibold text-green-700 dark:text-green-300">
                          File validation successful!
                        </h5>
                      </div>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        Your Excel file has passed all validation checks. Ready
                        to import.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setImportStep(1)}
                      className="px-4 py-2 text-gray-600 dark:text-dark-muted hover:text-gray-800 dark:hover:text-dark-text font-medium"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleImportSubmit}
                      disabled={importErrors.length > 0 || isImporting}
                      className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isImporting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />{" "}
                          Importing...
                        </>
                      ) : (
                        "Start Import"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {importStep === 3 && importResults && (
                <div className="space-y-6">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-dark-muted">
                    Import Results
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                        {importResults.success}
                      </p>
                      <p className="text-sm text-emerald-600 dark:text-emerald-300">
                        Successful
                      </p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                        {importResults.failed}
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-300">
                        Failed
                      </p>
                    </div>
                  </div>

                  {importResults.success > 0 && (
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle
                          size={18}
                          className="text-emerald-600 dark:text-emerald-400"
                        />
                        <h5 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                          Import Successful
                        </h5>
                      </div>
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">
                        {importResults.success} caregivers have been imported
                        successfully. You can edit their additional details in
                        the admin panel.
                      </p>
                    </div>
                  )}

                  {importResults.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <h5 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
                        Failed Imports ({importResults.errors.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto">
                        {importResults.errors
                          .slice(0, 3)
                          .map((error, index) => (
                            <div key={index} className="mb-2">
                              <p className="text-sm font-medium text-red-600 dark:text-red-300">
                                {error.row
                                  ? `Row ${error.row}`
                                  : error.phone
                                    ? `Phone: ${error.phone}`
                                    : "Error"}
                              </p>
                              <p className="text-xs text-red-500 dark:text-red-400">
                                {error.error}
                              </p>
                            </div>
                          ))}
                        {importResults.errors.length > 3 && (
                          <p className="text-sm text-red-600 dark:text-red-300">
                            ... and {importResults.errors.length - 3} more
                            errors
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                        These caregivers were not imported. Please check the
                        errors and try again.
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={closeImportModal}
                      className="px-4 py-2 text-gray-600 dark:text-dark-muted hover:text-gray-800 dark:hover:text-dark-text font-medium"
                    >
                      Close
                    </button>
                    <button
                      onClick={resetImport}
                      className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                    >
                      Import More
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {isImporting && (
              <div className="px-6 py-3 border-t border-accent dark:border-dark-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-dark-muted">
                    Importing caregivers...
                  </span>
                  <span className="text-sm font-medium text-text dark:text-dark-text">
                    {importProgress}%
                  </span>
                </div>
                <div className="h-2 bg-accent dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  {importProgress < 100
                    ? "Processing... This may take a moment."
                    : "Import completed!"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Stepper */}
      <div className="bg-card dark:bg-dark-surface rounded-2xl p-6 shadow-sm border border-accent dark:border-dark-border mb-8">
        <div className="flex items-center justify-between relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-accent dark:bg-gray-700 z-0"></div>
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-primary z-0 transition-all duration-500 ease-in-out"
            style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
          ></div>

          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className="relative z-10 flex flex-col items-center gap-2"
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300 ${
                  step <= currentStep
                    ? "bg-primary text-white shadow-lg shadow-primary/20 dark:shadow-primary/20"
                    : "bg-card dark:bg-dark-surface border-2 border-accent dark:border-dark-border text-gray-400"
                }`}
              >
                {step < currentStep ? <Check size={20} /> : step}
              </div>
              <span
                className={`text-xs font-semibold uppercase tracking-wider ${
                  step <= currentStep
                    ? "text-primary dark:text-primary"
                    : "text-gray-400 dark:text-dark-muted"
                }`}
              >
                {step === 1
                  ? "Basic Info"
                  : step === 2
                    ? "Service Details"
                    : "Personal Details"}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Form Content */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-accent dark:border-dark-border overflow-hidden">
        <div className="p-8 md:p-10">
          {/* --- STEP 1: Basic Info --- */}
          {currentStep === 1 && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <h2 className="text-xl font-bold text-text dark:text-dark-text mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Basic Information
              </h2>

              {/* Image Upload */}
              <div className="flex justify-center mb-10">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-full bg-accent dark:bg-gray-700 flex items-center justify-center border-4 border-card dark:border-gray-600 shadow-lg overflow-hidden">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Camera size={32} className="text-gray-400" />
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2.5 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.firstName
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.lastName
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.lastName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Full Name (Amharic)
                  </label>
                  <input
                    type="text"
                    name="nameAmh"
                    value={formData.nameAmh}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    placeholder="Enter full name in Amharic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.email
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="name@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.phone
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="0911..."
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Alternative Phone Number
                  </label>
                  <input
                    type="tel"
                    name="alternativePhone"
                    value={formData.alternativePhone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.alternativePhone
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Alternative phone"
                  />
                  {errors.alternativePhone && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.alternativePhone}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Password{" "}
                    {!isEditMode && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.password
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder={
                      isEditMode
                        ? "Leave blank to keep current password"
                        : "Min. 6 characters"
                    }
                  />
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </p>
                  )}
                  {isEditMode && (
                    <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                      Leave blank to keep current password
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Service Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 appearance-none transition-all ${
                        errors.serviceType
                          ? "border-red-500 focus:ring-red-200"
                          : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                      }`}
                    >
                      <option value="">Select Service Type</option>
                      {serviceCategories.map((cat) => (
                        <option key={cat.id} value={cat.label}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    <ChevronRight
                      className="absolute right-4 top-1/2 -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none"
                      size={16}
                    />
                  </div>
                  {errors.serviceType && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.serviceType}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Experience (Years) <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.experience
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                  >
                    <option value="">Select Experience</option>
                    <option value="0">No experience</option>
                    <option value="below_1">Below 1 year</option>
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                      <option key={year} value={year}>
                        {year} {year === 1 ? "Year" : "Years"}
                      </option>
                    ))}
                  </select>
                  {errors.experience && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.experience}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Desired Salary <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    min="0"
                    step="100"
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all ${
                      errors.salary
                        ? "border-red-500 focus:ring-red-200"
                        : "border-accent dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Salary"
                  />
                  {errors.salary && (
                    <p className="text-red-500 text-xs mt-1">{errors.salary}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Time Preference
                  </label>
                  <select
                    name="dateTimePreference"
                    value={formData.dateTimePreference}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 transition-all focus:ring-primary/20 focus:border-primary"
                  >
                    <option value="full-time">Full time</option>
                    <option value="part-time">Part-time</option>
                    <option value="on-demand">On-demand</option>
                    <option value="living-in">Living-in</option>
                    <option value="night-shift">Night-shift</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center cursor-pointer select-none">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={formData.isAvailable}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            isAvailable: e.target.checked,
                          }))
                        }
                        className="sr-only"
                      />
                      <div
                        className={`block w-14 h-8 rounded-full transition-colors ${
                          formData.isAvailable
                            ? "bg-primary"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      ></div>
                      <div
                        className={`dot absolute left-1 top-1 bg-white dark:bg-gray-200 w-6 h-6 rounded-full transition-transform ${
                          formData.isAvailable ? "transform translate-x-6" : ""
                        }`}
                      ></div>
                    </div>
                    <div className="ml-3 text-sm font-semibold text-text dark:text-dark-text">
                      Available for Booking
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* --- STEP 2: Service Details --- */}
          {currentStep === 2 && (
            <Step2ServiceDetails
              categoryFields={categoryFields}
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              categoryLabel={formData.serviceType}
              errors={errors}
            />
          )}

          {/* --- STEP 3: Personal Details --- */}
          {currentStep === 3 && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-8">
              <h2 className="text-xl font-bold text-text dark:text-dark-text mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Personal & Professional Details
              </h2>

              {/* Personal Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option>Female</option>
                    <option>Male</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    max={
                      new Date(
                        new Date().setFullYear(new Date().getFullYear() - 18),
                      )
                        .toISOString()
                        .split("T")[0]
                    }
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Rating
                  </label>
                  <select
                    name="rating"
                    value={formData.rating}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Nationality
                  </label>
                  <select
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option>Ethiopian</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Nationality (Amharic)
                  </label>
                  <input
                    type="text"
                    name="nationalityAmh"
                    value={formData.nationalityAmh}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Nationality in Amharic"
                  />
                </div>
              </div>

              {/* Languages Multi-Select */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-3">
                  Languages Spoken <span className="text-red-500">*</span>
                </label>
                {errors.languages && (
                  <p className="text-red-500 text-sm mb-2">
                    {errors.languages}
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {LANGUAGES.map((lang) => (
                    <div
                      key={lang}
                      onClick={() => toggleLanguage(lang)}
                      className={`cursor-pointer px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                        formData.languages.includes(lang)
                          ? "bg-primary text-white border-primary"
                          : "bg-card text-gray-600 border-accent hover:border-primary dark:bg-dark-surface dark:text-dark-muted dark:border-dark-border"
                      }`}
                    >
                      {lang}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-3">
                  Languages Spoken (Amharic)
                </label>
                <div className="flex flex-wrap gap-3">
                  {LANGUAGES_AMH.map((lang) => (
                    <div
                      key={lang}
                      onClick={() => toggleLanguageAmh(lang)}
                      className={`cursor-pointer px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                        formData.languagesAmh.includes(lang)
                          ? "bg-primary text-white border-primary"
                          : "bg-card text-gray-600 border-accent hover:border-primary dark:bg-dark-surface dark:text-dark-muted dark:border-dark-border"
                      }`}
                    >
                      {lang}
                    </div>
                  ))}
                </div>
              </div>

              {/* Address Section */}
              <div className="bg-accent dark:bg-gray-900/50 p-6 rounded-2xl border border-accent dark:border-dark-border">
                <h3 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wide mb-4">
                  Address Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Subcity <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="subcity"
                      value={formData.subcity}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-xl border bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary ${
                        errors.subcity
                          ? "border-red-500"
                          : "border-accent dark:border-dark-border"
                      }`}
                    >
                      <option value="">Select Subcity</option>
                      {SUBCITIES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    {errors.subcity && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.subcity}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Address Line 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-xl border bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary ${
                        errors.addressLine1
                          ? "border-red-500"
                          : "border-accent dark:border-dark-border"
                      }`}
                      placeholder="e.g., Bole, Megenagna"
                    />
                    {errors.addressLine1 && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.addressLine1}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                      Enter area/location (e.g., Bole, Megenagna)
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="e.g., 4 kilo, House No. 123"
                    />
                    <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                      Enter specific address details (e.g., 4 kilo, House No.)
                    </p>
                  </div>
                </div>

                <h3 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wide mt-6 mb-4">
                  Address Information (Amharic)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Address Line 1 (Amharic)
                    </label>
                    <input
                      type="text"
                      name="addressLine1Amh"
                      value={formData.addressLine1Amh}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="ለምሳሌ: ቦሌ, መገናኛ"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Address Line 2 (Amharic)
                    </label>
                    <input
                      type="text"
                      name="addressLine2Amh"
                      value={formData.addressLine2Amh}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="ለምሳሌ: 4 ኪሎ, ቤት ቁጥር 123"
                    />
                  </div>
                </div>
              </div>

              {/* Education */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Education Level <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="educationLevel"
                    value={formData.educationLevel}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.educationLevel
                        ? "border-red-500"
                        : "border-accent dark:border-dark-border"
                    }`}
                  >
                    <option value="">Select Level</option>
                    {EDUCATION_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  {errors.educationLevel && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.educationLevel}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Field of Study
                  </label>
                  <input
                    type="text"
                    name="educationField"
                    value={formData.educationField}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., Computer Science"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                    Education (Amharic)
                  </label>
                  <input
                    type="text"
                    name="educationAmh"
                    value={formData.educationAmh}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Education in Amharic"
                  />
                </div>
              </div>

              {/* About Section */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                  About (Bio)
                </label>
                <textarea
                  name="about"
                  value={formData.about}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Short biography..."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                  About (Amharic)
                </label>
                <textarea
                  name="aboutAmh"
                  value={formData.aboutAmh}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Short biography in Amharic..."
                />
              </div>

              {/* Course Details */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                  Has Course?
                </label>
                <select
                  value={formData.hasCourse ? "Yes" : "No"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      hasCourse: e.target.value === "Yes",
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
              {formData.hasCourse && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Course / Certification Details
                    </label>
                    <input
                      type="text"
                      name="courseDetails"
                      value={formData.courseDetails}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="List relevant certifications..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                      Course / Certification Details (Amharic)
                    </label>
                    <input
                      type="text"
                      name="courseDetailsAmh"
                      value={formData.courseDetailsAmh}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="List relevant certifications in Amharic..."
                    />
                  </div>
                </>
              )}

              {/* Referee Info */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                  Has Referee?
                </label>
                <select
                  value={formData.hasReferee ? "Yes" : "No"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      hasReferee: e.target.value === "Yes",
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-accent dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
              {formData.hasReferee && (
                <div className="bg-primary/10 dark:bg-primary/20 p-6 rounded-2xl border border-primary/20 dark:border-primary/30">
                  <h3 className="text-sm font-bold text-primary dark:text-primary uppercase tracking-wide mb-4">
                    Referee Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                        Referee Name
                      </label>
                      <input
                        type="text"
                        name="refereeName"
                        value={formData.refereeName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-primary/20 bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                        Referee Name (Amharic)
                      </label>
                      <input
                        type="text"
                        name="refereeNameAmh"
                        value={formData.refereeNameAmh}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-primary/20 bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
                        Referee Phone
                      </label>
                      <input
                        type="tel"
                        name="refereePhone"
                        value={formData.refereePhone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-primary/20 bg-card dark:bg-dark-surface text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-8 py-6 bg-accent dark:bg-gray-900 border-t border-accent dark:border-dark-border flex justify-between items-center">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-2.5 rounded-xl border border-accent dark:border-dark-border text-text dark:text-dark-text font-medium hover:bg-card dark:hover:bg-dark-surface transition-colors flex items-center gap-2"
            >
              <ChevronLeft size={18} /> Back
            </button>
          ) : (
            <div></div>
          )}

          <button
            type="button"
            onClick={currentStep === 3 ? handleSubmit : handleNext}
            disabled={submitting}
            className="px-8 py-3 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {submitting
              ? "Submitting..."
              : currentStep === 3
                ? isEditMode
                  ? "Update Caregiver"
                  : "Submit Caregiver"
                : "Next Step"}
            {!submitting && currentStep !== 3 && <ChevronRight size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCaregiver;
